import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

import { SharedDialogComponent, SharedDialogOptions } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { FormFieldComponent } from 'src/app/ui/form-field/form-field.component';
import {
  FilterDialogConfig,
  FilterDialogData,
  FilterField,
  FilterFieldOption,
} from './filter-dialog.models';

@Component({
  selector: 'app-filter-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    MatAutocompleteModule,
    SharedDialogComponent,
    FormFieldComponent,
  ],
  templateUrl: './filter-dialog.component.html',
  styleUrls: ['./filter-dialog.component.scss'],
})
export class FilterDialogComponent implements OnInit, OnDestroy {
  config: FilterDialogConfig;
  values: Record<string, any> = {};

  dialogOptions: SharedDialogOptions = {
    titleIcon: 'filter_icon',
    cancelText: 'CLOSE',
    cancelButtonStyle: 'secondary-btn',
    middleText: 'RESET FILTERS',
    middleIcon: 'rollback',
    middleButtonStyle: 'secondary-btn',
    confirmText: 'APPLY',
    confirmButtonStyle: 'primary',
    size: 'large',
  };

  autocompleteControls: Record<string, FormControl> = {};
  autocompleteResults: Record<string, Observable<FilterFieldOption[]>> = {};

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<FilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FilterDialogData,
  ) {
    this.config = data.config;
  }

  ngOnInit(): void {
    this.values = { ...(this.data.values || {}) };
    this.initAutocompleteFields();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initAutocompleteFields(): void {
    for (const section of this.config.sections) {
      for (const field of section.fields) {
        if (field.type === 'autocomplete' && field.searchFn) {
          const existing = this.values[field.key] as FilterFieldOption | undefined;
          const ctrl = new FormControl(existing?.label || '');
          this.autocompleteControls[field.key] = ctrl;

          this.autocompleteResults[field.key] = ctrl.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((term) => {
              const keyword = typeof term === 'string' ? term : '';
              if (!keyword || keyword.length < 2) return of([]);
              return field.searchFn!(keyword);
            }),
            takeUntil(this.destroy$),
          );
        }
      }
    }
  }

  onAutocompleteSelected(field: FilterField, option: FilterFieldOption): void {
    this.values[field.key] = option;
  }

  clearAutocomplete(field: FilterField): void {
    this.values[field.key] = undefined;
    this.autocompleteControls[field.key]?.setValue('');
  }

  displayAutocompleteFn(option: FilterFieldOption | string): string {
    if (!option) return '';
    return typeof option === 'string' ? option : option.label;
  }

  getActiveFilterCount(): number {
    let count = 0;
    for (const section of this.config.sections) {
      for (const field of section.fields) {
        if (field.type === 'range' || field.type === 'date-range') {
          if (this.values[field.minKey!] != null && this.values[field.minKey!] !== '') count++;
          if (this.values[field.maxKey!] != null && this.values[field.maxKey!] !== '') count++;
        } else if (field.type === 'multi-select' || field.type === 'checkbox') {
          const val = this.values[field.key];
          if (Array.isArray(val) && val.length > 0) count++;
        } else if (field.type === 'autocomplete') {
          const val = this.values[field.key];
          if (val && val.value != null) count++;
        } else {
          const val = this.values[field.key];
          if (val != null && val !== '' && val !== undefined) count++;
        }
      }
    }
    return count;
  }

  toggleMultiOption(field: FilterField, optionValue: string | number): void {
    const current: (string | number)[] = this.values[field.key] || [];
    const idx = current.indexOf(optionValue);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(optionValue);
    }
    this.values[field.key] = [...current];
  }

  isOptionSelected(field: FilterField, optionValue: string | number): boolean {
    const current: (string | number)[] = this.values[field.key] || [];
    return current.includes(optionValue);
  }

  toggleButtonOption(field: FilterField, optionValue: string | number): void {
    if (this.values[field.key] === optionValue) {
      this.values[field.key] = undefined;
    } else {
      this.values[field.key] = optionValue;
    }
  }

  clearAll(): void {
    for (const section of this.config.sections) {
      for (const field of section.fields) {
        if (field.type === 'range' || field.type === 'date-range') {
          this.values[field.minKey!] = undefined;
          this.values[field.maxKey!] = undefined;
        } else if (field.type === 'multi-select' || field.type === 'checkbox') {
          this.values[field.key] = [];
        } else if (field.type === 'autocomplete') {
          this.values[field.key] = undefined;
          this.autocompleteControls[field.key]?.setValue('');
        } else {
          this.values[field.key] = undefined;
        }
      }
    }
  }

  apply(): void {
    this.dialogRef.close(this.values);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
